import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  IBudgetsSetting,
  IItemBudget,
  PostLineBody,
  TotalSummary,
  TypeTax,
} from '@tacliatech/types';
import { of } from 'rxjs';

export interface TaxBreakdownItem {
  type: string;
  percentage: number;
  base: number;
  total: number;
}

export interface SummaryTotal {
  taxBreakdown: Array<{
    type: string;
    percentage: number;
    base: number;
    total: number;
  }>;
  subTotal: number;
  total: number;
  discount: number;
  totalTaxes: number;
}

@Injectable({
  providedIn: 'root',
})
export class BudgetCalculationsService {
  constructor(
    private readonly http: HttpClient,
    private i18n: TranslateService
  ) {}

  async calculateTotalsRealTime(
    itemsParam: IItemBudget[],
    settings: IBudgetsSetting
  ): Promise<SummaryTotal> {
    let subTotal = 0;
    let taxBreakdownMethod: Array<TaxBreakdownItem> = [];
    let totalMethod = 0;
    let discountMethod = 0;
    let totalTaxesMethod = 0;

    try {
      if (!itemsParam?.length || itemsParam.every((item) => !item.price)) {
        return Promise.resolve({
          taxBreakdown: [],
          subTotal: 0,
          total: 0,
          discount: 0,
          totalTaxes: 0,
        });
      }
      const result = await this.getSummaryTotals(itemsParam, settings);
      if (!result || !result.content || !result.content.total)
        throw new Error('No result from getSummaryTotals');

      const {
        content: {
          total: { taxableBase, total, taxAmount, discount, taxBreakdown },
        },
      } = result;

      discountMethod = this.parseNumberBySetting(
        settings,
        discount?.amount ?? 0
      );
      subTotal = this.parseNumberBySetting(settings, taxableBase?.amount ?? 0);
      totalMethod = this.parseNumberBySetting(settings, total?.amount ?? 0);
      totalTaxesMethod = this.parseNumberBySetting(
        settings,
        taxAmount?.amount ?? 0
      );

      taxBreakdownMethod = taxBreakdown?.map((tax) => {
        const taxMap = {
          [TypeTax.IVA]: 'budgets.settings.create.form.lbiva',
          [TypeTax.REC]: 'budgets.settings.create.form.lblrec-eq',
          [TypeTax.RET]: 'budgets.settings.create.form.lbretention',
          [TypeTax.IEPS]: 'budgets.settings.create.form.lbiva',
        };
        return {
          type: tax.tax?.type,
          percentage: tax.tax?.percentage,
          title: `${this.i18n.instant(taxMap[tax.tax?.type])} ${
            tax.tax?.percentage
          }%`,
          base: this.parseNumberBySetting(
            settings,
            tax.taxableBase?.amount ?? 0
          ),
          total: this.parseNumberBySetting(
            settings,
            tax.taxAmount?.amount ?? 0
          ),
        };
      });
    } catch (e) {
      // Handle error
    }
    return {
      taxBreakdown: taxBreakdownMethod,
      subTotal,
      total: totalMethod,
      discount: discountMethod,
      totalTaxes: totalTaxesMethod,
    };
  }

  calculateRetentions(items) {
    let Aiva: any[] = [];
    let Aret: any[] = [];
    let Arec: any[] = [];
    items?.forEach((item) => {
      // @ts-ignore
      let totalItem = item.price * item.quantity;
      // @ts-ignore
      if (item.discount > 0) {
        // @ts-ignore
        totalItem = totalItem * (1 - item.discount / 100);
      }

      // @ts-ignore
      if (item.taxes?.length > 0) {
        // @ts-ignore
        item.taxes.forEach((tax) => {
          if (tax !== null) {
            // @ts-ignore
            const value = (totalItem * tax.value) / 100;
            if (tax.type === TypeTax.IVA) {
              Aiva = this.findKey(tax.value, value, Aiva);
            }

            if (tax.type === TypeTax.REC) {
              Arec = this.findKey(tax.value, value, Arec);
            }

            if (tax.type === TypeTax.RET) {
              Aret = this.findKey(tax.value, value, Aret);
            }
          }
        });
      }
    });
    return of({ Arec, Aret, Aiva });
  }

  parseItemsToLineBody(
    items: IItemBudget[],
    settings: IBudgetsSetting
  ): PostLineBody[] {
    const lines: PostLineBody[] = [];

    const filteredItems = items?.filter((fil) => fil.type !== 'category');

    filteredItems?.forEach((item) => {
      const lineItem: PostLineBody = {
        description: item.description,
        quantity: Number(item.quantity) || 1,
        price: {
          amount: Number(item.price) || 0,
          currency:
            settings?.budgetPreferences.currency === 1
              ? { code: 'EUR', symbol: '€' }
              : { code: 'USD', symbol: '$' },
        },
        discount: item.discount ? item.discount : 0,
        taxes: (item.taxes ?? []).map((tax) => ({
          type: tax.type.toString(),
          percentage: tax.value,
        })),
        conceptName: item.concept,
      };
      lines.push(lineItem);
    });

    return lines;
  }

  async getSummaryTotals(itemsParam: IItemBudget[], settings: IBudgetsSetting) {
    const lines = this.parseItemsToLineBody(itemsParam, settings);
    return await this.getTotalSummary(lines).toPromise();
  }

  getTotalSummary(lines: PostLineBody[]) {
    return this.http.post<TotalSummary>(
      `:API_BASE_URLsales/billing-document-evaluations`,
      {
        lines: lines,
        disbursements: [],
      }
    );
  }

  parseNumberBySetting(settings: IBudgetsSetting, number: number): number {
    number = parseFloat(number.toFixed(settings?.budgetPreferences?.decimals));
    return number;
  }

  findKey(key, value, object: any[]) {
    let found = false;
    for (const item of object) {
      if (item.key === key) {
        item.value = item.value + value;
        found = true;
        break;
      }
    }
    if (!found) {
      object.push({ key: key, value: value });
    }
    return object;
  }
}
